class GalleryHeader extends HTMLElement {
  connectedCallback() {
    this.updateScrolledState = () => this.classList.toggle('is-scrolled', window.scrollY > 24);
    this.updateHeaderHeight = () => {
      document.documentElement.style.setProperty('--site-header-height', `${this.getBoundingClientRect().height}px`);
    };
    this.updateScrolledState();
    this.updateHeaderHeight();
    window.addEventListener('scroll', this.updateScrolledState, { passive: true });
    window.addEventListener('resize', this.updateHeaderHeight, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.updateHeaderHeight);
      this.resizeObserver.observe(this);
    }

    this.toggle = this.querySelector('[data-menu-toggle]');
    this.menu = this.querySelector('[data-mobile-menu]');
    if (!this.toggle || !this.menu) return;

    this.toggle.addEventListener('click', () => {
      const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
      this.toggle.setAttribute('aria-expanded', String(!isOpen));
      this.menu.hidden = isOpen;
      document.documentElement.toggleAttribute('scroll-lock', !isOpen);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.updateScrolledState);
    window.removeEventListener('resize', this.updateHeaderHeight);
    this.resizeObserver?.disconnect();
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.featured = this.querySelector('[data-featured-image]');
    this.buttons = this.querySelectorAll('[data-gallery-thumb]');
    if (!this.featured || this.buttons.length === 0) return;

    this.onClick = (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      const arrow = event.target.closest('[data-gallery-step]');
      if (thumb) this.select(Array.from(this.buttons).indexOf(thumb));
      if (arrow) {
        const current = Array.from(this.buttons).findIndex((button) => button.getAttribute('aria-current') === 'true');
        this.select(current + Number(arrow.dataset.galleryStep));
      }
    };
    this.addEventListener('click', this.onClick);
  }

  select(index) {
    const selected = ((index % this.buttons.length) + this.buttons.length) % this.buttons.length;
    const button = this.buttons[selected];
    this.featured.src = button.dataset.galleryImage;
    this.featured.srcset = button.dataset.gallerySrcset || '';
    this.featured.alt = button.dataset.galleryAlt || '';
    this.buttons.forEach((item) => item.setAttribute('aria-current', String(item === button)));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }
}

class LanguageSelector extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.options = Array.from(this.querySelectorAll('[data-language-option]'));
    this.menu = this.querySelector('[data-language-menu]');
    if (!this.form || this.options.length === 0) return;

    this.form.addEventListener('submit', () => {
      try {
        if (!this.detecting) window.localStorage.setItem('rlf-language-selected', 'true');
      } catch (error) {
        // The language switch still works when browser storage is unavailable.
      }
    });
    this.onOutsideClick = (event) => { if (!this.contains(event.target) && this.menu) this.menu.open = false; };
    this.onKeyDown = (event) => {
      if (event.key === 'Escape' && this.menu?.open) {
        this.menu.open = false;
        this.menu.querySelector('summary').focus();
      }
    };
    document.addEventListener('click', this.onOutsideClick);
    this.addEventListener('keydown', this.onKeyDown);

    if (document.documentElement.hasAttribute('data-language-detected')) return;
    document.documentElement.setAttribute('data-language-detected', '');
    this.detectPreferredLanguage();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutsideClick);
    this.removeEventListener('keydown', this.onKeyDown);
  }

  detectPreferredLanguage() {
    let languageSelected = false;
    let languageDetected = false;

    try {
      languageSelected = window.localStorage.getItem('rlf-language-selected') === 'true';
      languageDetected = window.sessionStorage.getItem('rlf-language-detected') === 'true';
    } catch (error) {
      // Continue without persistence when browser storage is unavailable.
    }

    if (languageSelected || languageDetected || window.Shopify?.locale !== 'nl') return;

    try {
      window.sessionStorage.setItem('rlf-language-detected', 'true');
    } catch (error) {
      // Detection remains safe because it only runs on the primary locale.
    }

    const available = new Set(this.options.map((option) => option.value.toLowerCase()));
    const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
    const browserLanguage = browserLanguages
      .map((language) => language?.toLowerCase().split('-')[0])
      .find((language) => available.has(language));
    const countryLanguages = {
      AT: 'de',
      AU: 'en',
      CA: 'en',
      DE: 'de',
      ES: 'es',
      FR: 'fr',
      GB: 'en',
      IE: 'en',
      IT: 'it',
      NL: 'nl',
      NZ: 'en',
      US: 'en',
    };
    const multilingualCountries = {
      BE: ['nl', 'fr'],
      CH: ['de', 'fr', 'it'],
    };
    const country = window.Shopify?.country;
    const countryLanguage = multilingualCountries[country]?.includes(browserLanguage)
      ? browserLanguage
      : countryLanguages[country] || multilingualCountries[country]?.[0];
    const preferredLanguage = countryLanguage || browserLanguage;

    if (!preferredLanguage || !available.has(preferredLanguage) || preferredLanguage === window.Shopify.locale) return;

    const option = this.options.find((button) => button.value === preferredLanguage);
    if (option) {
      this.detecting = true;
      this.form.requestSubmit(option);
    }
  }
}

class ArtworkPurchase extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.inputs = Array.from(this.querySelectorAll('input[type="radio"][name="id"]'));
    this.price = this.querySelector('[data-variant-price]');
    this.button = this.querySelector('[data-purchase-button]');
    if (!this.form || !this.button || this.inputs.length === 0) return;
    this.onChange = (event) => {
      if (!this.inputs.includes(event.target)) return;
      this.updateVariant(event.target, true);
    };
    this.onPopState = () => {
      const id = new URL(window.location.href).searchParams.get('variant');
      const selected = this.inputs.find((input) => input.value === id) || this.inputs[0];
      selected.checked = true;
      this.updateVariant(selected, false);
    };
    this.form.addEventListener('change', this.onChange);
    window.addEventListener('popstate', this.onPopState);
  }

  updateVariant(input, updateUrl) {
    const available = input.dataset.available === 'true';
    this.price.textContent = input.dataset.price;
    this.button.disabled = !available;
    this.button.textContent = available ? this.button.dataset.availableLabel : this.button.dataset.unavailableLabel;
    const checkout = this.querySelector('[data-dynamic-checkout]');
    if (checkout) checkout.hidden = !available;
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', input.value);
      window.history.replaceState(null, '', url);
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.onChange);
    window.removeEventListener('popstate', this.onPopState);
  }
}

class CollectionBrowser extends HTMLElement {
  connectedCallback() {
    this.sidebar = this.querySelector('.collection-sidebar');
    this.toggle = this.querySelector('[data-drawer-toggle]');
    this.curated = this.hasAttribute('data-curated');
    this.form = this.querySelector('form');
    this.grid = this.querySelector('.collection-page__grid');
    this.cards = Array.from(this.querySelectorAll('[data-artwork]'));
    this.count = this.querySelector('[data-result-count]');
    this.empty = this.querySelector('[data-empty-results]');
    this.sort = this.form.querySelector('[name="sort_by"]');
    this.defaultSort = this.sort?.dataset.defaultSort;
    this.collectionInputs = Array.from(this.form.querySelectorAll('[name="rlf_collection"]'));
    this.filterReset = this.querySelector('[data-filter-reset]');
    this.sortReset = this.querySelector('[data-sort-reset]');
    this.onToggle = () => this.setDrawer(!this.classList.contains('is-filter-open'));
    this.onClose = () => this.setDrawer(false);
    this.onKeyDown = (event) => {
      if (event.key === 'Escape') this.setDrawer(false);
      if (event.key === 'Tab' && this.classList.contains('is-filter-open') && window.matchMedia('(max-width: 760px)').matches) {
        const focusable = Array.from(this.sidebar.querySelectorAll('button:not([disabled]):not([hidden]), input:not([disabled]), select'));
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    this.onSubmit = (event) => {
      if (this.curated) {
        event.preventDefault();
        if (this.form.reportValidity()) this.update(true);
      }
    };
    this.onChange = (event) => {
      const field = event.target;
      if (field.name === 'rlf_collection') {
        if (!this.curated) {
          window.location.assign(field.dataset.collectionUrl);
          return;
        }
        if (field.value === 'all' && field.checked) this.collectionInputs.forEach((input) => { input.checked = input.value === 'all'; });
        else if (field.value !== 'all') this.collectionInputs.find((input) => input.value === 'all').checked = false;
        if (!this.collectionInputs.some((input) => input.checked)) this.collectionInputs.find((input) => input.value === 'all').checked = true;
      }
      if (!this.form.reportValidity()) return;
      if (this.curated) this.update(true);
      else this.form.requestSubmit();
    };
    this.onInput = (event) => {
      if (event.target.type !== 'number' || !this.form.checkValidity()) return;
      if (this.curated) this.update(true, true);
      else {
        clearTimeout(this.inputTimer);
        this.inputTimer = setTimeout(() => this.form.requestSubmit(), 450);
      }
    };
    this.onFilterReset = () => {
      if (!this.curated) {
        const url = new URL(this.collectionInputs.find((input) => input.value === 'all')?.dataset.collectionUrl || this.form.action, window.location.origin);
        if (this.sort?.value) url.searchParams.set('sort_by', this.sort.value);
        window.location.assign(url);
        return;
      }
      for (const field of this.form.elements) {
        if (field.name === 'sort_by') continue;
        if (field.type === 'checkbox') field.checked = field.name === 'rlf_collection' && field.value === 'all';
        else if (field.name) field.value = '';
      }
      this.update(true);
    };
    this.onSortReset = () => {
      this.sort.value = this.defaultSort;
      if (this.curated) this.update(true);
      else this.form.requestSubmit();
    };
    this.onPopState = () => { if (this.curated) { this.readUrl(); this.update(); } };
    this.toggle.addEventListener('click', this.onToggle);
    this.closeControls = this.querySelectorAll('[data-drawer-close], [data-drawer-dismiss]');
    this.closeControls.forEach((button) => button.addEventListener('click', this.onClose));
    this.addEventListener('keydown', this.onKeyDown);
    this.form.addEventListener('submit', this.onSubmit);
    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('input', this.onInput);
    this.filterReset.addEventListener('click', this.onFilterReset);
    this.sortReset.addEventListener('click', this.onSortReset);
    window.addEventListener('popstate', this.onPopState);
    if (this.curated) { this.readUrl(); this.update(); }
    else this.updateResetButtons();
  }

  setDrawer(open) {
    this.classList.toggle('is-filter-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.sidebar.setAttribute('aria-hidden', String(!open));
    this.sidebar.inert = !open;
    if (open) this.sidebar.querySelector('[data-drawer-close]').focus({ preventScroll: true });
    else this.toggle.focus({ preventScroll: true });
  }

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    this.form.reset();
    for (const field of this.form.elements) {
      if (!field.name || !params.has(field.name)) continue;
      if (field.type === 'checkbox') field.checked = params.getAll(field.name).includes(field.value);
      else if (field.tagName === 'SELECT') {
        field.value = Array.from(field.options).some((option) => option.value === params.get(field.name)) ? params.get(field.name) : this.defaultSort;
      } else field.value = params.get(field.name);
    }
    if (!this.collectionInputs.some((input) => input.checked && !input.disabled)) {
      this.collectionInputs.forEach((input) => { input.checked = input.value === 'all'; });
    }
  }

  updateResetButtons() {
    this.sortReset.hidden = !this.sort || this.sort.value === this.defaultSort;
    this.filterReset.hidden = !Array.from(this.form.elements).some((field) => {
      if (!field.name || field.name === 'sort_by') return false;
      if (field.name === 'rlf_collection') return field.checked && field.value !== 'all';
      if (field.type === 'checkbox') return field.checked;
      return field.value !== '';
    });
  }

  update(pushUrl = false, replaceUrl = false) {
    const values = new FormData(this.form);
    const min = values.get('rlf_min') ? Math.round(Number(values.get('rlf_min')) * 100) : 0;
    const max = values.get('rlf_max') ? Math.round(Number(values.get('rlf_max')) * 100) : Infinity;
    const selectedCollections = values.getAll('rlf_collection');
    const sort = values.get('sort_by');
    const cards = [...this.cards];
    if (sort === 'price-ascending') cards.sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));
    if (sort === 'price-descending') cards.sort((a, b) => Number(b.dataset.price) - Number(a.dataset.price));
    if (sort === 'title-ascending') cards.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title, document.documentElement.lang));
    if (sort === 'title-descending') cards.sort((a, b) => b.dataset.title.localeCompare(a.dataset.title, document.documentElement.lang));
    if (sort === 'created-descending') cards.sort((a, b) => Number(b.dataset.created) - Number(a.dataset.created));
    let count = 0;
    for (const card of cards) {
      const prices = card.dataset.prices ? JSON.parse(card.dataset.prices) : [Number(card.dataset.price)];
      const matchesPrice = prices.some((price) => price >= min && price <= max);
      const matchingVariant = JSON.parse(card.dataset.variants || '[]')
        .filter((variant) => variant.price >= min && variant.price <= max)
        .sort((a, b) => a.price - b.price)[0];
      const link = card.querySelector?.('.product-card');
      const priceLabel = card.querySelector?.('.price');
      if (matchingVariant && link && priceLabel) {
        const prefix = !values.get('rlf_min') && !values.get('rlf_max') && priceLabel.dataset?.fromLabel;
        priceLabel.textContent = prefix ? `${prefix} ${matchingVariant.money}` : matchingVariant.money;
        link.href = values.get('rlf_min') || values.get('rlf_max') ? matchingVariant.url : card.dataset.productUrl;
      }
      const matchesCollection = selectedCollections.length === 0 || selectedCollections.includes('all') || selectedCollections.some((handle) => card.dataset.collections.split(' ').includes(handle));
      card.hidden = !matchesPrice || !matchesCollection;
      if (!card.hidden) count += 1;
      this.grid.append(card);
    }
    this.count.textContent = (count === 1 ? this.count.dataset.one : this.count.dataset.other).replace('[count]', count);
    this.empty.hidden = count !== 0;
    this.updateResetButtons();
    if (pushUrl) {
      const url = new URL(window.location.href);
      for (const name of ['rlf_min', 'rlf_max', 'rlf_available', 'rlf_collection', 'sort_by', 'page']) url.searchParams.delete(name);
      for (const [name, value] of values) if (value && value !== this.defaultSort && value !== 'all') url.searchParams.append(name, value);
      window.history[replaceUrl ? 'replaceState' : 'pushState'](null, '', url);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.inputTimer);
    this.toggle?.removeEventListener('click', this.onToggle);
    this.closeControls?.forEach((button) => button.removeEventListener('click', this.onClose));
    this.removeEventListener('keydown', this.onKeyDown);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.form?.removeEventListener('change', this.onChange);
    this.form?.removeEventListener('input', this.onInput);
    this.filterReset?.removeEventListener('click', this.onFilterReset);
    this.sortReset?.removeEventListener('click', this.onSortReset);
    window.removeEventListener('popstate', this.onPopState);
  }
}

customElements.define('collection-browser', CollectionBrowser);
customElements.define('gallery-header', GalleryHeader);
customElements.define('product-gallery', ProductGallery);
customElements.define('language-selector', LanguageSelector);
customElements.define('artwork-purchase', ArtworkPurchase);
